import { Injectable, signal } from '@angular/core';
import { BsApiService } from '@brightside-web/desktop/data-access/core-services';
import { BehaviorSubject, Observable, Subject, catchError, map, of, tap } from 'rxjs';
import {
  CashAdvance,
  CashAdvanceGating,
  CashAdvancePayload,
  CashAdvancePost,
  CashAdvanceRepayment,
  CashAdvanceRepaymentStatus,
  CashAdvanceStatus,
} from './cash-advance.model';
import { SimpleGlobalModalService, GenericResponseCode } from '@brightside-web/desktop/data-access/core-services';

export enum UpdateCashAdvanceAction {
  PAUSE,
  RESUME,
}

@Injectable()
export class CashAdvanceService {
  gatingShown = false;

  private _cashAdvanceGating = signal<CashAdvanceGating | null>(null);
  cashAdvanceGating = this._cashAdvanceGating.asReadonly();

  private _storedCashAdvance: CashAdvance | undefined;

  private _processing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly processing$: Observable<boolean> = this._processing$.asObservable();

  private _requestCashSuccess$: Subject<boolean> = new Subject<boolean>();
  public readonly requestCashSuccess$: Observable<boolean> = this._requestCashSuccess$.asObservable();

  private _updateCashAdvanceSuccess$: Subject<UpdateCashAdvanceAction> = new Subject<UpdateCashAdvanceAction>();
  public readonly updateCashAdvanceSuccess$: Observable<UpdateCashAdvanceAction> = this._updateCashAdvanceSuccess$.asObservable();

  private _cashAdvanceInfo$: Subject<CashAdvance | undefined> = new Subject<CashAdvance | undefined>();
  public readonly cashAdvanceInfo$: Observable<CashAdvance | undefined> = this._cashAdvanceInfo$.asObservable();

  private _cashAdvanceError$: Subject<void> = new Subject<void>();
  public readonly cashAdvanceError$: Observable<void> = this._cashAdvanceError$.asObservable();



  constructor(private _bsApi: BsApiService, private _modalService: SimpleGlobalModalService) {}

  getCashAdvances(force = false): void {
    if (force) this.clearStoredCashAdvance();
    if (!this._storedCashAdvance) {
      this._bsApi
        .get<CashAdvancePayload>('api-mobile', '/accounts/spending/cash-advance')
        .pipe(
          map((res) => res.payload),
          tap((res) => {
            if (res) {
              this._cashAdvanceInfo$.next(res);
              if (res.gating) {
                this._cashAdvanceGating.set(res.gating);
              } else {
                this._cashAdvanceGating.set(null);
              }
              this._storedCashAdvance = res;
            } else {
              this._cashAdvanceInfo$.next(undefined);
            }
          }),
          catchError(() => of(this._cashAdvanceError$.next()))
        )
        .subscribe();
    } else {
      setTimeout(() => {
        this._cashAdvanceInfo$.next(this._storedCashAdvance);
      }, 0);
    }
  }

  clearStoredCashAdvance(): void {
    this._storedCashAdvance = undefined;
  }

  acceptCashAdvance(amount: number) {
    const gating = this.cashAdvanceGating();
    this._processing$.next(true);

    const postBody: CashAdvancePost = {
      amount,
      disbursement_destination_type: 'spending',
    };

    if (gating) {
      postBody.gating = {
        version: gating.version,
        decisionId: gating.decisionId,
        shown: this.gatingShown
      };
    }

    this._bsApi
      .post('api-mobile', '/accounts/spending/cash-advance', postBody)
      .subscribe({
        next: (resp) => {
          if (resp.result.code === GenericResponseCode.SERVICE_OK) {
            this._requestCashSuccess$.next(true);
          }
          this._processing$.next(false);
        },
        error: (err) => {
          this._processing$.next(false);
          this.showGeneralError();
        }
      });
  };

  updateCashAdvance(action: UpdateCashAdvanceAction, cashAdvanceId: number, repaymentId: number): void {
    this._processing$.next(true);

    const repay_optout = action === UpdateCashAdvanceAction.PAUSE ? true : false;

    this._bsApi
      .patch('api-mobile', '/accounts/spending/cash-advance', {
        cash_advance_id: cashAdvanceId,
        cash_advance_repayment_id: repaymentId,
        repay_optout,
      })
      .subscribe({
        next: (resp) => {
          if (resp.result.code === GenericResponseCode.SERVICE_OK) {
            this._updateCashAdvanceSuccess$.next(action);
          }
          this._processing$.next(false);
        },
        error: (error) => {
          this._processing$.next(false);
          this.showGeneralError();
        }
      });
  }

  showGeneralError() {
    this._modalService.createGenericErrorModal();
  }

  canDoCashAdvance(): Observable<boolean> {
    if (this._storedCashAdvance) {
      return of(this._storedCashAdvance.is_eligible || this._storedCashAdvance.existing_cash_advances);
    } else {
      return this._bsApi.get<CashAdvancePayload>('api-mobile', '/accounts/spending/cash-advance').pipe(
        map((res) => res.payload),
        map((res) => res.is_eligible || res.existing_cash_advances)
      );
    }
  }

  canRequestCashAdvance(): Observable<boolean> {
    if (this._storedCashAdvance) {
      return of(
        !this._storedCashAdvance.latest_cash_advance_details?.status ||
          this._storedCashAdvance.latest_cash_advance_details?.status === CashAdvanceStatus.REPAID
      );
    } else {
      return this._bsApi.get<CashAdvancePayload>('api-mobile', '/accounts/spending/cash-advance').pipe(
        map((res) => res.payload),
        map(
          (res) =>
            !res.latest_cash_advance_details?.status || res.latest_cash_advance_details?.status === CashAdvanceStatus.REPAID
        )
      );
    }
  }

  getStatusLabelName(status: string) {
    let statusLabel = '';
    switch (status) {
      case CashAdvanceRepaymentStatus.PLANNED:
      case CashAdvanceRepaymentStatus.PARTIAL_DEDUCTION_TAKEN:
        statusLabel = 'CASH_ADVANCE_REPAYMENT_SCHEDULED';
        break;
      case CashAdvanceRepaymentStatus.MISSED:
        statusLabel = 'CASH_ADVANCE_REPAYMENT_MISSED';
        break;
      case CashAdvanceRepaymentStatus.COMPLETED:
        statusLabel = 'CASH_ADVANCE_REPAYMENT_COMPLETE';
        break;
      case CashAdvanceRepaymentStatus.PARTIAL_COMPLETED:
        statusLabel = 'CASH_ADVANCE_REPAYMENT_PARTIAL';
        break;
      case CashAdvanceRepaymentStatus.SENT_TO_PAYROLL:
      case CashAdvanceRepaymentStatus.DEDUCTION_TAKEN:
        statusLabel = 'CASH_ADVANCE_REPAYMENT_PROCESSING';
        break;
      case CashAdvanceRepaymentStatus.CANCELED:
      case CashAdvanceRepaymentStatus.OPTED_OUT:
        statusLabel = 'CASH_ADVANCE_REPAYMENT_STATUS_PAUSED';
        break;
      default:
        statusLabel = '';
        break;
    }
    return statusLabel;
  }

  canPauseCashAdvance(repayOptout: boolean, currentRepaymentObj: CashAdvanceRepayment): boolean {
    if (!repayOptout && currentRepaymentObj.status === CashAdvanceRepaymentStatus.PLANNED) {
      return true;
    }
    return false;
  }

  canResumeCashAdvance(
    repaymentId: number,
    repayOptout: boolean,
    cashAdvanceRepatments: CashAdvanceRepayment[],
    currentRepaymentObj: CashAdvanceRepayment
  ): boolean {
    const [cashAdvanceRepaymentObject] = cashAdvanceRepatments;
    if (
      repayOptout &&
      cashAdvanceRepaymentObject.cash_repayment_id === repaymentId &&
      currentRepaymentObj.status === CashAdvanceRepaymentStatus.OPTED_OUT
    ) {
      return true;
    }
    return false;
  }

}
